import { current, isDraft } from '@reduxjs/toolkit';
import {
  IFeature,
  ILov,
  ILovDetail,
  IProductHierarchy,
  IProperty,
  ISelectedTreeItem,
} from '../../types';
import { IPropAssociation } from '../../types/IRequestTypes';
import { EMasterPropertyCodes, EPropertyApplicability, EPropertyType } from '../data/Constants';

//Formats the data to be sent in Property Association Request
export function getPropAssocRequest(material: ISelectedTreeItem): IPropAssociation {
  if (material.featureCode && material.productCode) {
    //Read and Save Associations Request for Options
    return {
      productCode: material.productCode,
      featureCode: material.featureCode,
      optionCode: material.code,
    };
  } else if (material.productCode) {
    //Read and Save Associations Request for Features
    return {
      productCode: material.productCode,
      featureCode: material.code,
    };
  } else {
    //Read and Save Associations Request for Product
    return {
      productCode: material.code,
    };
  }
}

// Removes keys with empty string or null values from an object
export function removeEmptyValues(obj: { [key: string]: string | null }): {
  [k: string]: string | null;
} {
  return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== '' && v != null));
}

/**
 * Checks if a property is Master
 * @param {IProperty} property The property to check
 * @returns {boolean} True if the property's applicability includes Master, false otherwise
 */
export function checkIfMasterProperty(property: IProperty): boolean {
  return property.applicability.includes(EPropertyApplicability.Master);
}

/**
 * This function maps the list of possible values available to enum type properties
 * Exceptional case: AGC property is converted from String to Enum type with product hierarchy as LOV
 *
 * @param {IProperty[]} properties Properties that contains the details of each property
 * @param {IProductHierarchy[]} productHierarchy Product hierarchy list used for AGCode dropdown
 * @param {ILov[]} listOfValues List of values for properties
 * @returns {IProperty[]} Properties data with LOV mappings
 */
export function preparePropertiesLov(
  properties: IProperty[],
  productHierarchy: IProductHierarchy[],
  listOfValues: ILov[]
): IProperty[] {
  const propertiesData = structuredClone(isDraft(properties) ? current(properties) : properties);
  propertiesData.forEach((prop: IProperty) => {
    // Exceptional case for AGC to show drop down.
    if (prop.code === EMasterPropertyCodes.AgCode && prop.type === EPropertyType.String) {
      prop.type = EPropertyType.Enum;
      prop.selectionType = EPropertyType.Single;
      const agcLov: ILovDetail[] = productHierarchy.map((product: IProductHierarchy) => ({
        code: product.ag,
        name: product.description,
      }));
      prop.listOfValues = agcLov || [];
    }
    if (prop.type === EPropertyType.Enum && prop.values) {
      const foundLov = listOfValues.find((lov: ILov) => lov.code === prop.values);
      prop.listOfValues = foundLov ? foundLov.options : [];
      prop.default = prop.default ? JSON.stringify([prop.default.code]) : null;
    }
  });
  return propertiesData;
}

/**
 * Returns true if any Enum property's stored association contains a code absent from its LOV,
 * or if the selected RCID code exists in the LOV but its release date is in a past month.
 * @param {IProperty[]} properties - Full list of properties to check.
 * @param assoc - Current association map of property codes to their stored JSON values.
 * @returns {boolean} True if at least one invalid value is present.
 */
export function hasInvalidPropertyAssociations(
  properties: IProperty[],
  assoc: { [key: string]: string | null } | undefined
): boolean {
  if (!assoc) return false;
  return properties
    .filter((p) => p.type === EPropertyType.Enum && p.listOfValues?.length) // only enum props with a defined LOV
    .some((p) => {
      const raw = assoc[p.code];
      if (!raw) return false; // no stored value — nothing to validate
      try {
        const codes = JSON.parse(raw) as string[];
        // flag if any stored code is absent from the LOV
        if (codes.some((code) => code && !p.listOfValues!.some((lov) => lov.code === code))) {
          return true;
        }
        // additional check for RCID: a valid code whose release date is already past is also invalid
        if (p.code === EMasterPropertyCodes.RCID && codes[0]) {
          const lovOption = p.listOfValues!.find((lov) => lov.code === codes[0]);
          return !!lovOption && isRcidOptionPastDated(lovOption);
        }
        return false;
      } catch {
        return false; // malformed JSON — skip silently
      }
    });
}

/**
 * Returns true if the RCID option's release date (the `name` field, YYYY-MM-DD)
 * is strictly before the current year-month.
 * @param {ILovDetail} option - The LOV entry whose `name` holds the release date.
 * @returns {boolean} True if the release date is in a past month.
 */
export function isRcidOptionPastDated(option: ILovDetail): boolean {
  if (!option?.name) return false;
  const releaseDate = new Date(option.name);
  if (Number.isNaN(releaseDate.getTime())) return false;
  const now = new Date();
  return (
    releaseDate.getFullYear() < now.getFullYear() ||
    (releaseDate.getFullYear() === now.getFullYear() && releaseDate.getMonth() < now.getMonth())
  );
}

/**
 * Check if PMP (Product Model Processor) property is active
 * @param {IFeature[]} features Features array to search through
 * @returns {boolean} True if PMP feature is active, false otherwise
 */
export function getPMPPropertyStatus(features: IFeature[]): boolean {
  let isActiveProduct = false;
  features.forEach((feature: IFeature) => {
    if (feature.code === 'PMP') {
      isActiveProduct = feature.isActive;
    }
  });
  return isActiveProduct;
}
